import React from 'react';
import * as PhosphorIcons from '@phosphor-icons/react';

const MedicalTermAnalyzerViewResult = ({ result }) => {
  const getContextClarityColor = (clarity) => {
    switch(clarity?.toLowerCase()) {
      case 'high':
        return 'bg-success/20 text-success';
      case 'medium':
        return 'bg-warning/20 text-warning';
      case 'low':
        return 'bg-error/20 text-error';
      default:
        return 'bg-base-200 text-base-content';
    }
  };

  const metadata = result?.metadata || {};
  const extractionMetrics = metadata?.extraction_metrics || {};
  const medicalTermAnalysis = result?.medical_term_analysis || {};
  const termsFound = medicalTermAnalysis?.terms_found || [];
  const termRelationships = medicalTermAnalysis?.term_relationships || [];

  return (
    <div className="mt-4 overflow-x-auto">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Left Column */}
        <div className="space-y-4">
          {/* Terms Found Card */}
          <div className="card bg-base-100 shadow-sm">
            <div className="card-body p-4">
              <div className="flex items-center gap-2 mb-4">
                <div className="p-2 rounded-lg bg-primary/10">
                  <PhosphorIcons.MagnifyingGlass size={20} weight="bold" className="text-primary"/>
                </div>
                <h3 className="card-title text-lg">Medical Terms Found</h3>
              </div>

              <div className="space-y-3">
                {termsFound.map((term, idx) => (
                  <div key={idx} className="p-3 bg-base-200 rounded-lg">
                    <div className="flex justify-between items-center mb-2">
                      <span className="font-medium">{term.term}</span>
                      <span className="px-2 py-0.5 rounded-full text-xs bg-primary/10">
                        {Math.round((term.confidence || 0) * 100)}%
                      </span>
                    </div>
                    
                    <div className="text-sm space-y-2">
                      <div className="flex items-center gap-2">
                        <span className="text-base-content/70">Category:</span>
                        <span className="capitalize">{term.category?.replace(/_/g, ' ')}</span>
                      </div>
                      
                      {term.details && (
                        <>
                          {term.details.specialty && (
                            <div className="flex items-center gap-2">
                              <span className="text-base-content/70">Specialty:</span>
                              <span className="capitalize">{term.details.specialty}</span>
                            </div>
                          )}
                          
                          {term.details.definition && (
                            <div className="text-base-content/70">
                              <span className="block mb-1">Definition:</span>
                              <p className="text-base-content">{term.details.definition}</p>
                            </div>
                          )}

                          {term.details.common_name && (
                            <div className="flex items-center gap-2">
                              <span className="text-base-content/70">Common Name:</span>
                              <span>{term.details.common_name}</span>
                            </div>
                          )}

                          {Array.isArray(term.details.related_terms) && term.details.related_terms.length > 0 && (
                            <div>
                              <span className="text-base-content/70 block mb-1">Related Terms:</span>
                              <div className="flex flex-wrap gap-2">
                                {term.details.related_terms.map((relatedTerm, idx) => (
                                  <span key={idx} className="px-2 py-0.5 bg-base-300 rounded-full text-xs">
                                    {relatedTerm}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Right Column */}
        <div className="space-y-4">
          {/* Analysis Metrics Card */}
          <div className="card bg-base-100 shadow-sm">
            <div className="card-body p-4">
              <div className="flex items-center gap-2 mb-4">
                <div className="p-2 rounded-lg bg-secondary/10">
                  <PhosphorIcons.ChartBar size={20} weight="bold" className="text-secondary"/>
                </div>
                <h3 className="card-title text-lg">Analysis Metrics</h3>
              </div>

              <div className="space-y-3">
                {/* Extraction Metrics */}
                <div className="p-3 bg-base-200 rounded-lg">
                  <span className="font-medium block mb-2">Extraction Quality</span>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between items-center">
                      <span className="text-base-content/70">Context Clarity</span>
                      <span className={`px-2 py-0.5 rounded-full text-xs ${getContextClarityColor(extractionMetrics.context_clarity)}`}>
                        {extractionMetrics.context_clarity || 'Unknown'}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-base-content/70">Confidence Score</span>
                      <span className="px-2 py-0.5 rounded-full text-xs bg-success/20 text-success">
                        {Math.round((extractionMetrics.confidence_score || 0) * 100)}%
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-base-content/70">Medical Accuracy</span>
                      <span className="px-2 py-0.5 rounded-full text-xs bg-success/20 text-success">
                        {Math.round((extractionMetrics.medical_accuracy || 0) * 100)}%
                      </span>
                    </div>
                  </div>
                </div>

                {/* Term Relationships */}
                {termRelationships.length > 0 && (
                  <div className="p-3 bg-base-200 rounded-lg">
                    <span className="font-medium block mb-2">Term Relationships</span>
                    <div className="space-y-3">
                      {termRelationships.map((relationship, idx) => (
                        <div key={idx} className="p-2 bg-base-300 rounded">
                          <div className="text-sm space-y-2">
                            <div>
                              <span className="text-base-content/70">Primary Term:</span>
                              <span className="ml-2 font-medium">{relationship.primary_term}</span>
                            </div>
                            
                            <div>
                              <span className="text-base-content/70 block mb-1">Related Terms:</span>
                              <div className="flex flex-wrap gap-2">
                                {relationship.related_terms.map((term, termIdx) => (
                                  <span key={termIdx} className="px-2 py-0.5 bg-primary/10 rounded-full text-xs">
                                    {term}
                                  </span>
                                ))}
                              </div>
                            </div>

                            <div className="flex items-center gap-2">
                              <span className="text-base-content/70">Relationship:</span>
                              <span className="capitalize">{relationship.relationship_type?.replace(/_/g, ' ')}</span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MedicalTermAnalyzerViewResult;